import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { firebaseAuth } from "@/lib/firebase";

/**
 * Platform-owner admin login. Separate from the merchant phone+OTP /login —
 * admins are Firebase email/password users seeded into ezydine.platform_admins.
 * On success we go to /superadmin; AdminRequireAuth then verifies membership.
 */
export default function SuperadminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Already signed in? Skip the form — the guard will validate admin rights.
  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged((u) => {
      if (u) navigate("/superadmin", { replace: true });
    });
    return unsub;
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    try {
      setIsLoading(true);
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
      navigate("/superadmin", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-foreground-950 px-4 overflow-hidden">
      {/* Ambient backdrop */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-primary-500/20 blur-3xl"></div>
      <div className="pointer-events-none absolute -bottom-48 -right-32 w-[520px] h-[520px] rounded-full bg-primary-300/10 blur-3xl"></div>

      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/images/logo.png" alt="EzyDinePro" className="h-12 w-auto mb-3" />
          <span className="px-2.5 py-1 rounded-full bg-primary-500/15 border border-primary-500/25 text-primary-200 text-[10px] font-label font-bold uppercase tracking-widest">
            Super Admin Console
          </span>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 shadow-2xl shadow-black/30">
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-1">
            Welcome back
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            Platform-owner access only.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="admin-email"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Email address
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-mail-line"></i>
                </span>
                <input
                  id="admin-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@ezydine.com"
                  required
                  className="w-full pl-10 pr-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="admin-password"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-lock-2-line"></i>
                </span>
                <input
                  id="admin-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  className="w-full pl-10 pr-11 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Signing in...
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line"></i>
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-foreground-500 mt-6">
          <i className="ri-lock-2-line"></i>
          Restricted area. Access is logged for audit.
        </p>
      </div>
    </div>
  );
}
